import { useState, useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import {
  Button,
  Slide,
  Fade,
  Typography,
  Paper,
  Grid,
  Container,
  Box,
} from "@mui/material";
import { Policy } from "../../../@lib/types";
import { useNotify } from "../../../components/@common/NotificationsProvider";
import { useAuthSession } from "../../../components/AuthSessionProvider";
import {
  Card,
  CardSkeleton,
  Header,
  LatestReport,
  TabsFilters,
  useLanguageTranslator,
  CTAContainer,
} from "../../../components";
import { Attachment } from "../../../features";
import {
  usePoliciesDocumentsCall,
  usePrivatePoliciesCall,
} from "../../../hooks";

const policyDeleteMutationDocument = gql`
  mutation policyDeleteMutation($policy: PolicyDeleteInput!) {
    policyDelete(input: $policy) {
      policy {
        id
      }
    }
  }
`;

function PrivatePolicies() {
  const [inputValue, setInputValue] = useState("");
  const [currTab, setCurrTab] = useState<"all">("all");
  const [attachmentDetails, setAttachmentDetails] = useState({ name: "" });

  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const { translator } = useLanguageTranslator();

  const { policyDocuments } = usePoliciesDocumentsCall({
    variables: { limit: 8 },
  }) as any;

  const {
    policies = [],
    dist,
    loading,
    refetch,
  } = usePrivatePoliciesCall({
    user,
    policyStatus: currTab,
    variables: { limit: 8 },
  });

  const [deletePolicy, { loading: deletingPolicy }] = useMutation(
    policyDeleteMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  /**
   * handleDeletePolicy()
   */
  const handleDeletePolicy = useCallback(
    async (policy: Pick<Policy, "id" | "title">) => {
      if (
        await notify.confirm(
          `Deleting policy "${policy.title}". This cannot be undone.`
        )
      ) {
        try {
          await deletePolicy({ variables: { policy: { id: policy.id } } });
          refetch();
        } catch (err) {
          notify.error(err as Error);
        }
      }
    },
    [deletePolicy, refetch, notify]
  );

  const privateTab = [
    {
      value: "approved",
      count: dist.approved.length,
      title: translator("status.Approved"),
    },
    {
      value: "pending",
      count: dist.pending.length,
      title: translator("status.Pending"),
    },
    {
      value: "rejected",
      count: dist.rejected.length,
      title: translator("status.Rejected"),
    },
  ];

  const filteredPoliciesList = useMemo(() => {
    if (!inputValue) return policies || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return policies?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, policies]);

  const filteredPolicyDocumentsList = useMemo(() => {
    if (!inputValue) return policyDocuments || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return policyDocuments?.filter((item: any) =>
      item?.filename
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, policyDocuments]);

  return (
    <Box pb={4}>
      <Header
        title={translator("Policies")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />

      <Container sx={{ my: 3 }}>
        <TabsFilters
          dist={dist}
          currTab={currTab}
          setCurrTab={setCurrTab}
          listItems={privateTab}
          heading={`${translator("Latest Policies")}`}
        />
        <Grid container spacing={2}>
          {filteredPoliciesList?.map((p: any, i: any) => (
            <Grid key={`${i}`} item xs={12} md={6} lg={3}>
              <Slide in timeout={180 * (i + 1)} direction="right">
                <Card
                  {...p}
                  path={`/policies/${p?.id}`}
                  editPath={`/submission/policies/${p?.id}/edit`}
                  disabled={deletingPolicy}
                  onDelete={() => handleDeletePolicy(p)}
                  sx={{ height: "100%" }}
                  isPrivate={true}
                />
              </Slide>
            </Grid>
          ))}

          {loading &&
            filteredPoliciesList.length === 0 &&
            Array(4)
              .fill(null)
              .map((_, i) => (
                <Fade key={`${i}`} in timeout={180 * (i + 1)}>
                  <Grid item xs={12} md={6} lg={3}>
                    {CardSkeleton}
                  </Grid>
                </Fade>
              ))}
        </Grid>
      </Container>

      <Container sx={{ my: 3 }}>
        <Fade in timeout={800}>
          <CTAContainer sx={{ textAlign: "center" }}>
            <Typography variant="subtitle1" mb={2}>
              {translator("addNewItemToPlatform", {
                item: translator(`confirmationItems.policy`),
              })}
            </Typography>
            <Button
              component={RouterLink}
              to="new"
              sx={{
                px: 6,
                backgroundColor: "#ffffff",
                color: "primary.dark",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {translator("generic.CREATE")}
            </Button>
          </CTAContainer>
        </Fade>
      </Container>

      <LatestReport
        items={filteredPolicyDocumentsList}
        path="/policies/view-all-Reports"
      />

      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 3, mb: 2 }}>
          <Typography variant="h4">
            {translator("generic.Data")} {translator("generic.Import")}
          </Typography>
        </Paper>

        <Attachment
          allowedFormats={["pdf"]}
          text="PDF file (max. 50mb)"
          setAttachmentDetails={setAttachmentDetails}
          attachmentDetails={attachmentDetails}
        />
        <Box textAlign="right">
          <Button variant="contained" sx={{ px: 6 }} disabled>
            {translator("generic.Import")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export { PrivatePolicies };
